/**
 * Quick filter chips under the search filter bar (Online, New, Verified,
 * Can message, Nearby). The chips live outside the filters modal, so its
 * "Clear all" resets them through this module.
 */

const RESET_PREFERENCES_URL = '/app/search/preferences/reset';
const RESET_TIMEOUT_MS = 3000;

/**
 * Turn off the Nearby chip and stop its hidden inputs from submitting.
 */
export function clearNearbyChip(nearbyEl) {
    const state = nearbyEl?._x_dataStack?.[0];
    if (state) {
        state.active = false;
        state.location = '';
    }
    nearbyEl?.querySelectorAll('input[name="location"], input[name="radius"]').forEach(input => {
        input.disabled = true;
    });
}

/**
 * Turn off every active quick chip in the search form.
 *
 * Returns true when the form renders a saved-preference chip
 * (`data-persist-field`). The caller must then reset the stored preferences,
 * because a bare visit seeds those chips from them, and the stored value can
 * be on while the chip shows off.
 */
export function clearQuickChips(form) {
    let hasPreferenceChips = false;

    form.querySelectorAll('[data-quick-chip]').forEach(chip => {
        if (chip.dataset.persistField) {
            hasPreferenceChips = true;
        }
        const state = chip._x_dataStack?.[0];
        if (!state?.on) return;
        state.on = false;
        // Alpine applies `:disabled` on its next tick. A submit before then sends the stale value.
        chip.querySelectorAll('input').forEach(input => {
            input.disabled = true;
        });
    });

    clearNearbyChip(form.querySelector('[x-data*="nearbyChip"]'));

    return hasPreferenceChips;
}

function postResetPreferences() {
    const body = new FormData();
    const token = document.querySelector('meta[name="csrf_token"]');
    if (token && token.content) {
        body.append('csrf_token', token.content);
    }
    return fetch(RESET_PREFERENCES_URL, {
        method: 'POST',
        body,
        headers: { 'X-Requested-With': 'XMLHttpRequest' },
        keepalive: true,
        credentials: 'same-origin',
    });
}

/**
 * Turn off the "Verified" and "Can message" preferences in one request.
 *
 * The promise always resolves, at the latest after RESET_TIMEOUT_MS, so a
 * stalled request cannot block the search. It resolves to true only when the
 * server confirmed the reset.
 */
export function resetSearchPreferences(post = postResetPreferences) {
    let timer;
    const request = Promise.resolve()
        .then(post)
        .then(response => Boolean(response?.ok && !response.redirected))
        .catch(() => false);
    const timeout = new Promise(resolve => {
        timer = setTimeout(() => resolve(false), RESET_TIMEOUT_MS);
    });
    return Promise.race([request, timeout]).then(confirmed => {
        clearTimeout(timer);
        return confirmed;
    });
}
